// Pages' dialogs (M7-02): New doc (DOCS-KB: a template picker, then title and space;
// `pages.create`, then the page opens), moving a page (another space and/or parent; `pages.move`, the server refuses a
// page under itself), a page's history (`pages.versions`, a version's read-only preview from
// `pages.version`, "Restore this version" → `pages.restore`, written as a new version) and the
// pages search (`pages.search`: title, space and the text around the hit).

import { PAGE_TEMPLATES, type PageTemplateKey } from "@companyos/domain-work/pages";
import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerTitle,
  EmptyState,
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  PageEditor,
  Skeleton,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { FileTextIcon, HistoryIcon, SearchIcon } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { docIcon } from "./doc-kinds";
import { type MemberInput, MemberSharesField, useMemberOptions } from "./doc-sharing";
import { useSeatPeople } from "./model";
import { DOC_LEVELS, type ShareInput, ShareSpacesDialog, SpaceSharesField } from "./space-shares";
import type { NewPageTarget, PageRow } from "./spaces-tree";
import {
  invalidateWork,
  pageMembersQuery,
  pageSearchQuery,
  pageSharesQuery,
  pageVersionQuery,
  pageVersionsQuery,
  spacesQuery,
  workKeys,
} from "./work-queries";

type Space = Awaited<ReturnType<Api["client"]["spaces"]["list"]>>["data"][number];
export type PageDetail = Awaited<ReturnType<Api["client"]["pages"]["get"]>>;

export const PAGE_KINDS = [
  { value: "page", label: "Page" },
  { value: "sop", label: "SOP" },
  { value: "policy", label: "Policy" },
  { value: "kb", label: "KB article" },
  { value: "vision", label: "Vision" },
  { value: "journal", label: "Journal" },
  { value: "decision", label: "Decision" },
  { value: "strategy", label: "Strategy" },
] as const;

export const kindLabel = (kind: string) =>
  kind === "task_body" ? "Task page" : (PAGE_KINDS.find((k) => k.value === kind)?.label ?? kind);

const problemText = (error: unknown, fallback: string) =>
  error instanceof ApiError
    ? (error.problem?.detail ?? error.problem?.title ?? fallback)
    : error
      ? fallback
      : null;

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Where a new doc goes: a space chosen in the dialog (`spaceId: null`), a space, or a page. */
export type NewDocTarget = Omit<NewPageTarget, "spaceId"> & { spaceId: string | null };

type TemplateChoice = "blank" | PageTemplateKey;
const TILES: { key: TemplateChoice; label: string; blurb: string; kind: string }[] = [
  { key: "blank", label: "Blank", blurb: "An empty page.", kind: "page" },
  ...(["sop", "policy", "kb", "meeting"] as const).map((key) => ({
    key,
    label: PAGE_TEMPLATES[key].label,
    blurb: PAGE_TEMPLATES[key].blurb,
    kind: PAGE_TEMPLATES[key].kind,
  })),
];

/**
 * DOCS-KB "New doc": a template picker (Blank, SOP, Policy, KB article, Meeting notes), then the
 * title and the space; Create makes it (`pages.create` with the template, whose kind it implies)
 * and opens it. The one way to make a doc: the Docs screen, the tree's "+", a space, a sub-page.
 */
export function NewDocDialog({
  api,
  company,
  target,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  /** Where the doc goes; null = closed. */
  target: NewDocTarget | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const spaces = useQuery({ ...spacesQuery(api, company.id), enabled: target !== null });
  const editable = (spaces.data?.data ?? []).filter((s) => !s.archived_at);
  const [choice, setChoice] = useState<TemplateChoice | null>(null);
  const [title, setTitle] = useState("");
  const [spaceId, setSpaceId] = useState("");
  // MULTI-SPACE-ACCESS: "Also share with" — more spaces, each with the level its members get.
  const [shares, setShares] = useState<ShareInput[]>([]);
  const ids = { title: useId(), space: useId() };
  useEffect(() => {
    if (!target) return;
    setChoice(null);
    setTitle("");
    setSpaceId(target.spaceId ?? "");
    setShares([]);
  }, [target]);
  const space = spaceId || editable[0]?.id || "";
  const sharing = shares.filter((s) => s.space_id !== space);
  const create = useMutation({
    mutationFn: (t: NewDocTarget) =>
      api.client.pages.create({
        params: { co: company.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          space_id: space,
          ...(t.parentPageId ? { parent_page_id: t.parentPageId } : {}),
          title: title.trim(),
          ...(choice && choice !== "blank" ? { template: choice } : { kind: "page" as const }),
          ...(sharing.length ? { shares: sharing } : {}),
        },
      }),
    onSuccess: async (page) => {
      toast.add({ title: `${page.title} created`, type: "success" });
      await invalidateWork(queryClient, company.id);
      onClose();
      void navigate({
        to: "/c/$slug/work/p/$pageId",
        params: { slug: company.slug, pageId: page.id },
      });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (target && space) create.mutate(target);
  };
  const error = problemText(create.error, "The doc could not be created.");
  const picked = TILES.find((t) => t.key === choice);
  return (
    <Dialog open={target !== null} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>New doc</DialogTitle>
          <DialogDescription>
            {target?.parentTitle
              ? `Under ${target.parentTitle}. It follows the space's access.`
              : "It follows its space's access."}
          </DialogDescription>
        </DialogHeader>
        {!picked ? (
          <ul aria-label="Templates" className="m-0 grid list-none grid-cols-2 gap-2 p-0">
            {TILES.map((t) => {
              const Icon = docIcon(t.kind);
              return (
                <li key={t.key}>
                  <button
                    type="button"
                    aria-label={t.label}
                    onClick={() => setChoice(t.key)}
                    className="flex h-full w-full items-start gap-3 rounded-lg border border-border bg-card p-3 text-left hover:bg-sunken focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-faint" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground">{t.label}</span>
                      <span className="block text-xs text-muted-foreground">{t.blurb}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <form onSubmit={submit} className="grid gap-4">
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor={ids.title}>Title</FieldLabel>
                <Input
                  id={ids.title}
                  required
                  autoFocus
                  maxLength={500}
                  placeholder={picked.label}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.space}>Space</FieldLabel>
                <NativeSelect
                  id={ids.space}
                  value={space}
                  disabled={Boolean(target?.parentPageId)}
                  onChange={(e) => setSpaceId(e.target.value)}
                >
                  {editable.map((s) => (
                    <NativeSelectOption key={s.id} value={s.id}>
                      {s.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <SpaceSharesField
                spaces={editable}
                homeSpaceId={space}
                value={sharing}
                onChange={setShares}
                what="doc"
                levels={DOC_LEVELS}
              />
              {error ? <FieldError>{error}</FieldError> : null}
            </FieldGroup>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setChoice(null)}>
                Back
              </Button>
              <Button type="submit" disabled={create.isPending || !title.trim() || !space}>
                Create
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * MULTI-SPACE-ACCESS: a doc's Share dialog — its home space (Move changes that) and the spaces it
 * is also shared into, each with a level (`pages.shares.set`).
 */
export function SharePageDialog({
  api,
  company,
  page,
  open,
  onOpenChange,
}: {
  api: Api;
  company: CompanyAccess;
  page: { id: string; title: string; space_id: string; kind?: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const spaces = useQuery({ ...spacesQuery(api, company.id), enabled: open });
  const shares = useQuery({ ...pageSharesQuery(api, company.id), enabled: open });
  const current = (shares.data?.data ?? []).filter((s) => s.object_id === page.id);
  // KB-UPLOAD-2: the people and DEs it is shared with directly, saved with the spaces.
  const saved = useQuery({ ...pageMembersQuery(api, company.id, page.id), enabled: open });
  const options = useMemberOptions(api, company, open);
  const [members, setMembers] = useState<MemberInput[]>([]);
  const savedKey = JSON.stringify(
    (saved.data?.data ?? []).map((m) => ({
      principal_kind: m.principal_kind,
      principal_id: m.principal_id,
      level: m.level,
    })),
  );
  useEffect(() => {
    if (open) setMembers(JSON.parse(savedKey) as MemberInput[]);
  }, [open, savedKey]);
  return (
    <ShareSpacesDialog
      open={open}
      onOpenChange={onOpenChange}
      title={page.title}
      what="doc"
      spaces={spaces.data?.data ?? []}
      homeSpaceId={page.space_id}
      current={current}
      ready={shares.isSuccess && spaces.isSuccess && saved.isSuccess}
      levels={DOC_LEVELS}
      save={async (next) => {
        // Codex r1: only what changed is sent (each has its own rights), and a saved half is
        // shown even when the other half is refused.
        const was = JSON.stringify(current.map((c) => ({ space_id: c.space_id, level: c.level })));
        if (JSON.stringify(next) !== was) {
          await api.client.pages.shares.set({
            params: { co: company.id, id: page.id },
            body: { shares: next },
          });
          await invalidateWork(queryClient, company.id);
        }
        if (JSON.stringify(members) !== savedKey)
          await api.client.pages.members.set({
            params: { co: company.id, id: page.id },
            body: { members },
          });
      }}
      onSaved={() => invalidateWork(queryClient, company.id)}
    >
      {(pending: boolean) => (
        <Field>
          <FieldLabel>People and DEs</FieldLabel>
          <MemberSharesField
            options={options}
            value={members}
            onChange={setMembers}
            defaultLevel={page.kind === "kb" ? "reader" : "editor"}
            disabled={pending || !saved.isSuccess}
            everyone={{ companyId: company.id, companyName: company.name }}
          />
        </Field>
      )}
    </ShareSpacesDialog>
  );
}

/** Move a page to another parent and/or space (its pages below move with it). */
export function MovePageDialog({
  api,
  company,
  page,
  spaces,
  pages,
  open,
  onOpenChange,
}: {
  api: Api;
  company: CompanyAccess;
  page: Pick<PageDetail, "id" | "title" | "space_id" | "parent_page_id">;
  spaces: Space[];
  pages: PageRow[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [spaceId, setSpaceId] = useState(page.space_id);
  const [parentId, setParentId] = useState(page.parent_page_id ?? "");
  const ids = { space: useId(), parent: useId() };
  useEffect(() => {
    if (!open) return;
    setSpaceId(page.space_id);
    setParentId(page.parent_page_id ?? "");
  }, [open, page.space_id, page.parent_page_id]);
  // The page itself and the pages below it are not offered (the server refuses them too).
  const below = new Set<string>([page.id]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const p of pages)
      if (p.parent_page_id && below.has(p.parent_page_id) && !below.has(p.id)) {
        below.add(p.id);
        grew = true;
      }
  }
  const parents = pages.filter((p) => p.space_id === spaceId && !below.has(p.id));
  const move = useMutation({
    mutationFn: () =>
      api.client.pages.move({
        params: { co: company.id, id: page.id },
        body: {
          ...(spaceId !== page.space_id ? { space_id: spaceId } : {}),
          parent_page_id: parentId || null,
        },
      }),
    onSuccess: async (p) => {
      toast.add({ title: `${p.title} moved`, type: "success" });
      await invalidateWork(queryClient, company.id);
      onOpenChange(false);
    },
  });
  const error = problemText(move.error, "The page could not be moved.");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            move.mutate();
          }}
          className="grid gap-4"
        >
          <DialogHeader>
            <DialogTitle>Move {page.title}</DialogTitle>
            <DialogDescription>
              The pages under it move with it. Under a restricted page it becomes restricted too.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.space}>Space</FieldLabel>
              <NativeSelect
                id={ids.space}
                value={spaceId}
                onChange={(e) => {
                  setSpaceId(e.target.value);
                  setParentId("");
                }}
              >
                {spaces
                  .filter((s) => !s.archived_at)
                  .map((s) => (
                    <NativeSelectOption key={s.id} value={s.id}>
                      {s.name}
                    </NativeSelectOption>
                  ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.parent}>Parent page</FieldLabel>
              <NativeSelect
                id={ids.parent}
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
              >
                <NativeSelectOption value="">None: the top of the space</NativeSelectOption>
                {parents.map((p) => (
                  <NativeSelectOption key={p.id} value={p.id}>
                    {p.title}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            {error ? <FieldError>{error}</FieldError> : null}
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={move.isPending}>
              Move page
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A page's versions, newest first; one opens read-only and can be restored. */
export function PageHistoryDrawer({
  api,
  company,
  page,
  open,
  onOpenChange,
}: {
  api: Api;
  company: CompanyAccess;
  page: PageDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const co = company.id;
  // Mounted only while open (the page screen), so the names' reads happen on demand.
  const people = useSeatPeople(api, co);
  const nameOf = (m: string | null, kind: string) =>
    kind === "system"
      ? "CompanyOS"
      : kind === "agency_user"
        ? "An agency operator"
        : people.nameOf(m).name;
  const queryClient = useQueryClient();
  const versions = useQuery({ ...pageVersionsQuery(api, co, page.id), enabled: open });
  const [picked, setPicked] = useState<number | null>(null);
  const preview = useQuery({
    ...pageVersionQuery(api, co, page.id, picked ?? 1),
    enabled: open && picked !== null,
  });
  useEffect(() => {
    if (!open) setPicked(null);
  }, [open]);
  const restore = useMutation({
    mutationFn: (version: number) =>
      api.client.pages.restore({ params: { co, id: page.id }, body: { version } }),
    onSuccess: async (p, version) => {
      queryClient.setQueryData(workKeys.page(co, p.id), p);
      toast.add({ title: `Version ${version} restored`, description: `Now version ${p.version}.` });
      await invalidateWork(queryClient, co);
      onOpenChange(false);
    },
  });
  const rows = versions.data?.data ?? [];
  return (
    <Drawer open={open} onOpenChange={onOpenChange} width="720px">
      <DrawerHeader>History</DrawerHeader>
      <DrawerBody>
        <DrawerTitle>History of {page.title}</DrawerTitle>
        {versions.isPending ? (
          <div role="status" aria-label="Loading the history" className="mt-4 flex flex-col gap-2">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : rows.length ? (
          <ol aria-label="Versions" className="m-0 mt-4 flex list-none flex-col gap-1 p-0">
            {rows.map((v) => (
              <li key={v.version}>
                <button
                  type="button"
                  aria-pressed={picked === v.version}
                  onClick={() => setPicked(v.version)}
                  className="flex w-full items-baseline gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-muted aria-pressed:bg-primary-soft"
                >
                  <span className="font-medium tabular-nums">v{v.version}</span>
                  <span className="min-w-0 flex-1 truncate text-secondary">
                    {v.title} · {nameOf(v.author_membership_id, v.author_kind)}
                    {v.restored_from_version ? ` · restored from v${v.restored_from_version}` : ""}
                    {v.version === page.version ? " · current" : ""}
                  </span>
                  <time dateTime={v.created_at} className="shrink-0 text-xs text-muted-foreground">
                    {when(v.created_at)}
                  </time>
                </button>
              </li>
            ))}
          </ol>
        ) : (
          <EmptyState
            variant="compact"
            icon={HistoryIcon}
            title="No versions yet"
            description="Each change of the title or body is kept here."
          />
        )}
        {picked !== null ? (
          <section aria-label={`Version ${picked}`} className="mt-6 border-t border-subtle pt-4">
            <div className="mb-3 flex items-center gap-2">
              <Badge variant="neutral">v{picked}</Badge>
              <span className="min-w-0 flex-1 text-sm text-muted-foreground">
                A read-only preview.
              </span>
              {page.can_edit && picked !== page.version ? (
                <Button
                  size="sm"
                  disabled={restore.isPending || !preview.data}
                  onClick={() => restore.mutate(picked)}
                >
                  Restore this version
                </Button>
              ) : null}
            </div>
            {preview.data ? (
              <PageEditor
                key={picked}
                title={preview.data.title}
                initialBlocks={preview.data.body}
                editable={false}
                label={`Version ${picked} body`}
              />
            ) : (
              <Skeleton className="h-24" />
            )}
            {restore.error ? (
              <p className="mt-2 text-sm text-danger-text">
                {problemText(restore.error, "The version could not be restored.")}
              </p>
            ) : null}
          </section>
        ) : null}
      </DrawerBody>
    </Drawer>
  );
}

/** Search the pages the caller can view (title, space, the text around the hit). */
export function PageSearchDialog({
  api,
  company,
  open,
  onOpenChange,
}: {
  api: Api;
  company: CompanyAccess;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const id = useId();
  useEffect(() => {
    const t = setTimeout(() => setQ(draft.trim()), 250);
    return () => clearTimeout(t);
  }, [draft]);
  useEffect(() => {
    if (!open) {
      setDraft("");
      setQ("");
    }
  }, [open]);
  const hits = useQuery({ ...pageSearchQuery(api, company.id, q), enabled: open && q !== "" });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Search pages</DialogTitle>
          <DialogDescription>Titles and text of the pages you can see.</DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={id} className="sr-only">
            Search pages
          </FieldLabel>
          <Input
            id={id}
            type="search"
            autoFocus
            placeholder="Search pages…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </Field>
        <div className="max-h-96 overflow-y-auto">
          {q === "" ? null : hits.isPending ? (
            <div role="status" aria-label="Searching" className="flex flex-col gap-2">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          ) : hits.data?.data.length ? (
            <ul aria-label="Search results" className="m-0 flex list-none flex-col gap-1 p-0">
              {hits.data.data.map((h) => (
                <li key={h.id}>
                  <Link
                    to="/c/$slug/work/p/$pageId"
                    params={{ slug: company.slug, pageId: h.id }}
                    search={keepFocus}
                    onClick={() => onOpenChange(false)}
                    className="flex gap-3 rounded-md px-3 py-2 hover:bg-muted"
                  >
                    <FileTextIcon
                      aria-hidden="true"
                      className="mt-0.5 size-4 shrink-0 text-faint"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{h.title}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {h.space_name} · {kindLabel(h.kind)}
                      </span>
                      {h.snippet ? (
                        <span className="mt-0.5 block text-xs text-secondary">…{h.snippet}…</span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              variant="compact"
              icon={SearchIcon}
              title="No pages match"
              description="Try another word from the title or the text."
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
